<template>
  <d-modal
    class="modal-big"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('customer.contact.addNewLabel') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-tabs v-if="allDataLoaded && !loadError">
        <d-tab
          :title="$t('ui.generalInfo')"
          active>
          <h5 class="mt-3 mb-3 pb-3 border-bottom d-flex">
            {{ $t('ui.generalInfo') }}
          </h5>
          <d-form>
            <validation-observer ref="contact-new-form" tag="form">
              <div class="form-group">
                <label>
                  {{ $t('customer.contact.contactDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :disabled-dates="futureDates"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="!orderID"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToOrder') }}:
                </label>

                <select-order
                  ref="orderSelect"
                  :orderID="null"
                  :customerID="customerID"/>
              </div>

              <div
                v-if="customerID"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToContact') }}:
                </label>

                <contact-customer-contact
                  ref="contactSelect"
                  :customerId="customerID"
                  :readOnly="false" />
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('customer.contact.contactType') }}:
                </label>

                <d-form-checkbox
                  v-for="(contactType, index) of contactTypes"
                  :key="'contact-type-checkbox-' + index"
                  inline
                  v-model="types"
                  :value="contactType.type">
                  {{ contactType.name }}
                </d-form-checkbox>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('customer.contact.descriptionOptional') }}:
                </label>

                <d-textarea
                  :value.prop="description"
                  v-model="description" />
              </div>
            </validation-observer>
          </d-form>
        </d-tab>
        <d-tab
          :title="$t('customer.contact.comments')"
          :disabled="true">
        </d-tab>
        <d-tab
          :title="$t('customer.contact.notifications')"
          :disabled="true">
        </d-tab>
      </d-tabs>

      <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-modal-body>
    <d-modal-footer v-if="allDataLoaded && !loadError">
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        :disabled="saveInProgress"
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        <div
          v-if="saveInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        <template v-if="!saveInProgress">
          {{ $t('ui.saveChanges') }}
        </template>
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import ContactCustomerContact from './ContactCustomerContact.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import SelectOrder from './../../components/common/SelectOrder.vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'contact-popup',
  props: [
    'orderID',
    'customerID'
  ],
  components: {
    'contact-customer-contact': ContactCustomerContact,
    'select-order': SelectOrder
  },
  computed: {
    allDataLoaded () {
      return this.loadedTypes;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    futureDates () {
      return {
        from: new Date()
      };
    }
  },
  data() {
    return {
      date: new Date(),
      customerContactID: null,
      types: ['PHONE'],
      description: '',
      saveInProgress: false,
      selectedOrderID: null,
      loadError: false,
      loadedTypes: false,
      contactTypes: []
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('ui-select-order-updated', this.updateOrder);
    this.$bus.$on('ui-select-customer-contact-updated', this.updateCustomerContact);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    handleClose () {
      this.$bus.$emit('close-order-contact-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['contact-new-form'], this.submitForm);
    },
    submitForm () {
      this.saveInProgress = true;

      let formDataToSend = {
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        customer_id: this.customerID,
        types: this.types,
        description: this.description
      };

      if (this.orderID) {
        formDataToSend.order_id = this.orderID;
      }

      if (this.selectedOrderID) {
        formDataToSend.order_id = this.selectedOrderID;
      }

      if (this.customerContactID) {
        formDataToSend.customer_contact_id = this.customerContactID;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/contacts/store',
        formData: formDataToSend,
        successTitle: this.$t('customer.contact.addContactSuccessTitle'),
        successText: this.$t('customer.contact.addContactSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.addContactErrorText'),
        successAction: () => {
          this.saveInProgress = false;
          this.$bus.$emit('contacts-refresh-list');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    updateOrder (newID) {
      this.selectedOrderID = newID;
    },
    updateCustomerContact (newID) {
      this.customerContactID = newID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-order-updated', this.updateOrder);
    this.$bus.$off('ui-select-customer-contact-updated', this.updateCustomerContact);
  }
};
</script>
